import React, {
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { scale } from '../../util/scale';
import { colors, spacing } from '../../style/theme';
import Times from '../svgs/Times';
import zIndex from '../../lib/zIndex';
import { css, PropsOf } from '@emotion/react';
import HorizontalSeparator from './HorizontalSeparator';

const darkener = scale({
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  bottom: 0,
  left: 0,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  padding: '5%',
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: zIndex('modalOverlay'),

  // hide the scrollbar
  scrollbarWidth: 'none',
  '-ms-overflow-style': 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const container = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minWidth: '100%',
  minHeight: 'calc(100% + 1px)', // This is a trick to activate the overscroll-behavior of its parent (the darkener), even when the modal doesn't overflow the viewport
});

type PopupWidth = 'default' | 'wide' | string | number;

const getMaxWidth = (width: PopupWidth) => {
  if (width === 'default') return '470px';
  if (width === 'wide') return ['95%', '95%', '900px'];
  return width;
};

const getModalStyle = (
  width: PopupWidth,
  textAlign: 'left' | 'center',
  fixedHeight = false,
  modalMaxHeight: string | number = '80vh'
) =>
  scale({
    backgroundColor: 'white',
    borderRadius: '6px',
    boxSizing: 'border-box',
    height: 'fit-content',
    maxWidth: getMaxWidth(width),
    ...(fixedHeight
      ? {
          maxHeight: modalMaxHeight,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      : {}),
    padding: fixedHeight
      ? 0
      : [
          `${spacing.xLarge}px ${spacing.large}px ${spacing.xLarge}px`,
          `${spacing.xLarge}px`,
        ],
    position: 'relative',
    textAlign,
  });

export default function Popup({
  children,
  className,
  closeIcon = <Times color={colors.textPrimary} size={spacing.default} />,
  onRequestClose,
  backdropContent,
  width = 'default',
  textAlign,
  fixedHeight = false,
  modalMaxHeight,
  ...restOfProps
}: {
  children?: ReactNode;
  className?: string;
  closeIcon?: ReactNode;
  backdropContent?: ReactNode;
  onRequestClose: (event: MouseEvent | KeyboardEvent) => void;
  /** Width of the popup: 'default' (470px), 'wide' (900px), or a custom value */
  width?: PopupWidth;
  /** Text alignment inside the popup (defaults to 'center' for default width, 'left' for wide) */
  textAlign?: 'left' | 'center';
  /** When true, caps modal height and scrolls content (e.g. long copy) */
  fixedHeight?: boolean;
  /** Overrides the fixed-height modal cap. */
  modalMaxHeight?: string | number;
} & HTMLAttributes<HTMLDivElement>) {
  // Default textAlign based on width if not explicitly provided
  const resolvedTextAlign = textAlign ?? (width === 'wide' ? 'left' : 'center');
  const modalRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const onRequestCloseRef = useRef(onRequestClose);
  onRequestCloseRef.current = onRequestClose;

  // Trap focus within modal
  useEffect(() => {
    // Store the previously focused element
    previousActiveElementRef.current =
      document.activeElement as HTMLElement | null;

    // Focus the modal when it opens
    const modalElement = modalRef.current;
    if (modalElement) {
      if (fixedHeight) {
        // Scrollable modal: avoid focusing a lower focusable (e.g. "Learn more")
        // which would scroll the dialog to the bottom on open.
        const scrollEl = scrollContainerRef.current;
        if (scrollEl) {
          scrollEl.scrollTop = 0;
        }
      }
      // Find first focusable element in modal
      const focusableElements = modalElement.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const focusOpts = fixedHeight ? { preventScroll: true } : undefined;
      if (firstFocusable) {
        firstFocusable.focus(focusOpts);
      } else {
        modalElement.focus(focusOpts);
      }
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onRequestCloseRef.current(event);
        return;
      }

      // Trap Tab key within modal
      if (event.key === 'Tab' && modalElement) {
        const focusableElements = Array.from(
          modalElement.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        );

        if (focusableElements.length === 0) return;

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    };

    if (document && document.addEventListener) {
      document.addEventListener('keydown', handleKeydown);
      return () => {
        document.removeEventListener('keydown', handleKeydown);
        // Restore focus to previously focused element
        if (previousActiveElementRef.current) {
          previousActiveElementRef.current.focus();
        }
      };
    }
  }, [fixedHeight]);

  return (
    <div
      css={darkener}
      onClick={(event) => {
        onRequestClose(event);
        event.stopPropagation();
      }}
      role="presentation"
      {...restOfProps}
    >
      <div css={container}>
        {backdropContent}
        <div
          ref={modalRef}
          className={className}
          css={getModalStyle(
            width,
            resolvedTextAlign,
            fixedHeight,
            modalMaxHeight
          )}
          onClick={(event) => {
            event.stopPropagation();
          }}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          {closeIcon && (
            <div
              css={
                fixedHeight
                  ? {
                      flexShrink: 0,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      background: 'white',
                    }
                  : {
                      position: 'absolute',
                      top: spacing.small,
                      right: spacing.small,
                    }
              }
            >
              <button
                css={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  lineHeight: 0,
                  padding: spacing.small,
                }}
                onClick={(event) => onRequestClose(event)}
                aria-label="Close dialog"
              >
                {closeIcon}
              </button>
            </div>
          )}
          {fixedHeight ? (
            <div
              ref={scrollContainerRef}
              css={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <div
                css={scale({
                  padding: [
                    `${spacing.xLarge}px ${spacing.large}px ${spacing.xLarge}px`,
                    `${spacing.xLarge}px`,
                  ],
                })}
              >
                {children}
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

export function PopupWithButtons({
  buttons,
  buttonAreaAttributes,
  children,
  contentAreaAttributes,
  ...restOfProps
}: {
  buttons: ReactNode;
  buttonAreaAttributes?: HTMLAttributes<HTMLDivElement>;
  contentAreaAttributes?: HTMLAttributes<HTMLDivElement>;
} & PropsOf<typeof Popup>) {
  return (
    <Popup
      css={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0 !important',
      }}
      {...restOfProps}
    >
      <div
        css={{ padding: spacing.default, paddingTop: spacing.medium }}
        {...contentAreaAttributes}
      >
        {children}
      </div>
      <HorizontalSeparator />
      <div
        css={{
          gap: spacing.default,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'right',
          padding: spacing.default,
        }}
        {...buttonAreaAttributes}
      >
        {buttons}
      </div>
    </Popup>
  );
}
